import { ISkippedJourneyHotelApiItem, QuotationCloneVersionResponseModel, QuotationDuplicateResponseModel } from "models/axiosModel"

const EMPTY_LABEL = "—"

/** One table row of the Hotel Availability Update popup: a hotel room and the days it was cleared from. */
export interface ISkippedHotelRow {
    key: string
    hotelName: string
    roomTypeName: string
    dayRangeLabel: string
}

const toLabel = (value: unknown): string => String(value ?? "").trim()

const toDayNumber = (value: unknown): number | null => {
    if (value == null || value === "") return null
    const day = Number(value)
    return Number.isFinite(day) ? day : null
}

/**
 * `skipped_hotels` from a duplicate or clone-version response; `[]` when missing, empty or not an array.
 * Read from `data` (documented) with a top-level fallback, since `requests.post` returns the raw body.
 */
export const readSkippedHotels = (
    response?: QuotationDuplicateResponseModel | QuotationCloneVersionResponseModel | null,
): ISkippedJourneyHotelApiItem[] => {
    const skipped = response?.data?.skipped_hotels ?? (response as any)?.skipped_hotels
    return Array.isArray(skipped) ? skipped.filter((item) => item && typeof item === "object") : []
}

/** Router `state` key carrying skipped hotels from the duplicate call to the new quotation's preview. */
export const SKIPPED_HOTELS_ROUTE_STATE_KEY = "duplicateSkippedHotels"

/** Route state for `navigate()`; `undefined` when nothing was skipped so no popup shows. */
export const buildSkippedHotelsRouteState = (
    skippedHotels: ISkippedJourneyHotelApiItem[],
): Record<string, ISkippedJourneyHotelApiItem[]> | undefined =>
    skippedHotels.length > 0 ? { [SKIPPED_HOTELS_ROUTE_STATE_KEY]: skippedHotels } : undefined

export const readSkippedHotelsFromRouteState = (state: unknown): ISkippedJourneyHotelApiItem[] => {
    const skipped = (state as Record<string, unknown> | null)?.[SKIPPED_HOTELS_ROUTE_STATE_KEY]
    return Array.isArray(skipped) ? skipped.filter((item) => item && typeof item === "object") : []
}

/** `[1, 2, 4]` → `"Day 1-2, 4"`; `[3]` → `"Day 3"`. */
export const formatDayRangeLabel = (dayNumbers: number[]): string => {
    const days = Array.from(new Set(dayNumbers)).sort((a, b) => a - b)
    if (days.length === 0) return EMPTY_LABEL

    const ranges: string[] = []
    let start = days[0]
    let prev = days[0]
    const pushRange = () => ranges.push(start === prev ? `${start}` : `${start}-${prev}`)

    days.slice(1).forEach((day) => {
        if (day === prev + 1) {
            prev = day
            return
        }
        pushRange()
        start = day
        prev = day
    })
    pushRange()

    return `Day ${ranges.join(", ")}`
}

/** One row per hotel room type (its days merged into ranges), ordered by the first affected day. */
export const buildSkippedHotelRows = (skippedHotels: ISkippedJourneyHotelApiItem[]): ISkippedHotelRow[] => {
    const groups = new Map<string, { hotelName: string; roomTypeName: string; days: number[]; order: number }>()

    skippedHotels.forEach((item, index) => {
        const hotelName = toLabel(item.hotel_name)
        const roomTypeName = toLabel(item.hotel_room_type_name)
        const groupKey = toLabel(item.hotel_room_type_id) || `${hotelName}|${roomTypeName}|${index}`
        const group = groups.get(groupKey) ?? { hotelName, roomTypeName, days: [], order: index }
        const day = toDayNumber(item.day_number)
        if (day !== null) group.days.push(day)
        groups.set(groupKey, group)
    })

    return Array.from(groups.entries())
        .map(([key, group]) => ({ key, ...group, firstDay: group.days.length ? Math.min(...group.days) : Infinity }))
        .sort((a, b) => a.firstDay - b.firstDay || a.order - b.order)
        .map(({ key, hotelName, roomTypeName, days }) => ({
            key,
            hotelName: hotelName || EMPTY_LABEL,
            roomTypeName,
            dayRangeLabel: formatDayRangeLabel(days),
        }))
}
